// src/components/home/awareness/RewardCodeAwarenessCampaign.js

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import api from '../../../api';

import RewardCodeAwarenessPopup from '../../popups/RewardCodeAwarenessPopup';

import {
  clearAwarenessCooldown,
  hasAwarenessCooldown,
  setAwarenessCooldown,
} from './awarenessStorage';

const CAMPAIGN_KEY = 'reward_code';

const SEVEN_DAYS_MS =
  7 * 24 * 60 * 60 * 1000;

const DEFAULT_OPEN_DELAY_MS = 900;

/*
 * Previous Reward popup localStorage key.
 *
 * We still read this key so users who passed before the manager
 * refactor do not immediately see the popup again.
 */
const getLegacyRewardPassKey = (username) => {
  return `bf_reward_popup_pass_until:${String(
    username || ''
  )
    .trim()
    .toLowerCase()}`;
};

const hasLegacyRewardCooldown = (username) => {
  try {
    const key = getLegacyRewardPassKey(username);
    const raw = localStorage.getItem(key);

    if (!raw) return false;

    const until = Number(raw);

    if (!Number.isFinite(until)) {
      localStorage.removeItem(key);
      return false;
    }

    if (until <= Date.now()) {
      localStorage.removeItem(key);
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

const clearLegacyRewardCooldown = (username) => {
  try {
    localStorage.removeItem(
      getLegacyRewardPassKey(username)
    );
  } catch {}
};

function RewardCodeAwarenessCampaign({
  user,
  enabled = false,
  onEligibilityResolved,
  onOpened,
  onFinished,
  openDelayMs = DEFAULT_OPEN_DELAY_MS,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [rewardCount, setRewardCount] = useState(0);

  /*
   * Prevent duplicate eligibility requests during one manager cycle.
   */
  const checkedRef = useRef(false);

  const username = String(user?.username || '')
    .trim()
    .toLowerCase();

  const accountType = String(
    user?.accountType || 'primary'
  ).toLowerCase();

  const resolveEligibility = useCallback(
    (eligible) => {
      onEligibilityResolved?.({
        campaignKey: CAMPAIGN_KEY,
        eligible: Boolean(eligible),
      });
    },
    [onEligibilityResolved]
  );

  /**
   * Reset all Reward state when the logged-in identity changes.
   */
  useEffect(() => {
    setIsOpen(false);
    setRewardCount(0);

    checkedRef.current = false;
  }, [username]);

  /**
   * Close safely when the manager disables this campaign.
   *
   * This may happen when:
   * - another Home drawer opens
   * - Reward finishes
   * - the manager moves to Banner
   *
   * This is not treated as the user pressing Pass.
   */
  useEffect(() => {
    if (enabled || !isOpen) return;

    setIsOpen(false);
  }, [enabled, isOpen]);

  /**
   * Prepare Reward for a later manager cycle.
   *
   * This is the important fix.
   *
   * After the five-minute global gap, the manager enables Reward
   * again so it can check priority first. Resetting checkedRef while
   * disabled allows that new check to happen.
   */
  useEffect(() => {
    if (enabled || isOpen) return;

    checkedRef.current = false;
  }, [enabled, isOpen]);

  /**
   * Check Reward eligibility only when enabled by the manager.
   */
  useEffect(() => {
    if (!username) return undefined;

    /*
     * Linked app identities do not receive personal plan rewards.
     */
    if (accountType === 'app') {
      setIsOpen(false);
      resolveEligibility(false);

      return undefined;
    }

    if (!enabled) {
      return undefined;
    }

    /*
     * Avoid duplicate checks while Reward remains enabled during
     * the current manager cycle.
     */
    if (checkedRef.current) {
      return undefined;
    }

    checkedRef.current = true;

    /*
     * Reward is currently inside either the new cooldown or the
     * legacy cooldown.
     *
     * Report false immediately so the manager can move to Banner.
     */
    const coolingDown =
      hasAwarenessCooldown(
        CAMPAIGN_KEY,
        username
      ) ||
      hasLegacyRewardCooldown(username);

    if (coolingDown) {
      setIsOpen(false);
      resolveEligibility(false);

      return undefined;
    }

    let cancelled = false;
    let timerId = null;

    const checkEligibility = async () => {
      try {
        const [planRes, codesRes] =
          await Promise.all([
            api.get('/plans/me'),
            api.get('/rewards/codes/me'),
          ]);

        if (cancelled) return;

        const planId = String(
          planRes?.data?.plan?.id || 'FREE'
        ).toUpperCase();

        const items = Array.isArray(
          codesRes?.data?.items
        )
          ? codesRes.data.items
          : [];

        const activeCodes = items.filter(
          (item) => {
            const status = String(
              item?.status || ''
            ).toLowerCase();

            const ownerHidden =
              Boolean(item?.ownerHiddenAt);

            return (
              status === 'active' &&
              !ownerHidden
            );
          }
        );

        const nextRewardCount =
          activeCodes.length;

        const eligible =
          planId === 'FREE' &&
          nextRewardCount > 0;

        setRewardCount(nextRewardCount);

        /*
         * Tell the manager whether Reward has priority.
         */
        resolveEligibility(eligible);

        if (!eligible) {
          setIsOpen(false);

          /*
           * Backend says the user is not eligible anymore.
           * Remove stale cooldown keys because they are unnecessary.
           */
          clearAwarenessCooldown(
            CAMPAIGN_KEY,
            username
          );

          clearLegacyRewardCooldown(username);

          return;
        }

        setIsOpen(true);

        onOpened?.({
          campaignKey: CAMPAIGN_KEY,
        });
      } catch (error) {
        console.error(
          'Failed to check reward awareness eligibility:',
          error
        );

        if (!cancelled) {
          setIsOpen(false);

          /*
           * Do not block Banner forever if Reward eligibility fails.
           */
          resolveEligibility(false);
        }
      }
    };

    timerId = window.setTimeout(() => {
      void checkEligibility();
    }, Math.max(0, Number(openDelayMs) || 0));

    return () => {
      cancelled = true;

      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [
    username,
    accountType,
    enabled,
    openDelayMs,
    onOpened,
    resolveEligibility,
  ]);

  /**
   * User presses Pass.
   */
  const handlePassed = useCallback(() => {
    if (!username) return;

    setAwarenessCooldown({
      campaignKey: CAMPAIGN_KEY,
      username,
      durationMs: SEVEN_DAYS_MS,
    });

    /*
     * Future cooldowns now use the shared awareness format.
     */
    clearLegacyRewardCooldown(username);

    setIsOpen(false);

    onFinished?.({
      campaignKey: CAMPAIGN_KEY,
      outcome: 'dismissed',
    });
  }, [
    onFinished,
    username,
  ]);

  /**
   * RewardCodeAwarenessPopup calls this after successful redemption.
   */
  const handleRedeemed = useCallback(
    (responseData) => {
      clearAwarenessCooldown(
        CAMPAIGN_KEY,
        username
      );

      clearLegacyRewardCooldown(username);

      setIsOpen(false);

      onFinished?.({
        campaignKey: CAMPAIGN_KEY,
        outcome: 'completed',
        response: responseData || null,
      });
    },
    [
      onFinished,
      username,
    ]
  );

  return (
    <RewardCodeAwarenessPopup
      open={isOpen}
      rewardCount={rewardCount}
      onPassed={handlePassed}
      onRedeemed={handleRedeemed}
    />
  );
}

export default RewardCodeAwarenessCampaign;
